import React, { FormEvent, useContext, useEffect, useState } from "react";

import ContentHeader from "../../components/ContentHeader";
import Loading from "../../components/Loading";
import Modal from "../../components/Modal";
import NotFound from "../NotFound";
import AuthContext from "../../contexts/AuthContext";
import Channel, { IChannelInput, IManagedChannel } from "../../services/api/Channel";
import ChannelType, { IChannelType } from "../../services/api/ChannelType";
import { RequestError } from "../../services/api/Request";
import SortableChannelList from "./components/SortableChannelList";

import "./assets/css/style.css";

interface ChannelFormState {
  name: string;
  typeId: string;
}

const emptyForm: ChannelFormState = { name: "", typeId: "" };

const errorMessage = (error: unknown, fallback: string) => {
  if (error instanceof Error && error.message) return error.message;
  return fallback;
};

const ChannelsManagement: React.FC = () => {
  const { user } = useContext(AuthContext);
  const [channels, setChannels] = useState<IManagedChannel[]>([]);
  const [channelTypes, setChannelTypes] = useState<IChannelType[]>([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<IManagedChannel | null>(null);
  const [form, setForm] = useState<ChannelFormState>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [togglingId, setTogglingId] = useState<number | null>(null);
  const [error, setError] = useState<string>("");
  const [notice, setNotice] = useState<string>("");
  const [showInactive, setShowInactive] = useState(false);
  const [pendingOrder, setPendingOrder] = useState<{ [typeId: number]: number[] }>({});
  const [savingOrderTypeId, setSavingOrderTypeId] = useState<number | null>(null);
  const [confirmModal, setConfirmModal] = useState<{ message: string; onOk: () => void } | null>(null);

  const isAdministrator = user?.profile.name === "Administrator";

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const [channelResponse, typeResponse] = await Promise.all([
        Channel.GetManagement(),
        ChannelType.GetAll()
      ]);
      setChannels(channelResponse);
      setChannelTypes(typeResponse);
    } catch (loadError) {
      setError(errorMessage(loadError, "Could not load channels."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdministrator) load();
  }, [isAdministrator]);

  if (!isAdministrator) return <NotFound />;

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setError("");
    setNotice("");
    setFormOpen(true);
  };

  const openEdit = (channel: IManagedChannel) => {
    setEditing(channel);
    setForm({
      name: channel.name,
      typeId: String(channel.typeId)
    });
    setError("");
    setNotice("");
    setFormOpen(true);
  };

  const closeForm = () => {
    setFormOpen(false);
    setEditing(null);
    setForm(emptyForm);
  };

  const submitChannel = async (data: IChannelInput) => {
    setSaving(true);
    setError("");
    setNotice("");
    try {
      if (editing) {
        await Channel.Update(editing.id, data);
        setNotice("Channel updated successfully.");
      } else {
        await Channel.Create(data);
        setNotice("Channel created successfully.");
      }
      closeForm();
      await load();
    } catch (submitError) {
      setError(errorMessage(submitError, "Could not save the channel."));
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const name = form.name.trim();
    const typeId = Number(form.typeId);

    if (!name) {
      setError("Name is required.");
      return;
    }
    if (!Number.isInteger(typeId) || typeId <= 0) {
      setError("Channel Type is required.");
      return;
    }

    const isDuplicate = channels.some((channel) =>
      channel.typeId === typeId
      && channel.name.trim().toLowerCase() === name.toLowerCase()
      && (!editing || channel.id !== editing.id)
    );
    if (isDuplicate) {
      setError("A channel with this name already exists for the selected channel type.");
      return;
    }

    const data: IChannelInput = { name, typeId };

    const channelTypeName = editing
      ? editing.channelTypeName
      : channelTypes.find((type) => type.id === typeId)?.name || "";
    const message = editing
      ? `Do you want to save the changes to the channel "${name}"?`
      : `Do you want to add the channel "${name}" to "${channelTypeName}"?`;

    setConfirmModal({ message, onOk: () => submitChannel(data) });
  };

  const performToggleActive = async (channel: IManagedChannel) => {
    setTogglingId(channel.id);
    setError("");
    setNotice("");
    try {
      await Channel.ToggleActive(channel.id);
      setNotice(`Channel ${channel.active ? "deactivated" : "activated"} successfully.`);
      await load();
    } catch (toggleError) {
      const requestError = toggleError as RequestError;
      const message = errorMessage(requestError, "Could not change the channel status.");
      if (requestError.status === 404) {
        await load();
      }
      setError(message);
    } finally {
      setTogglingId(null);
    }
  };

  const toggleActive = (channel: IManagedChannel) => {
    const message = channel.active
      ? `Do you want to deactivate the channel "${channel.name}"?`
      : `Do you want to activate the channel "${channel.name}"?`;

    setConfirmModal({ message, onOk: () => performToggleActive(channel) });
  };

  const handleOrderChange = (typeId: number, orderedIds: number[]) => {
    setPendingOrder({ ...pendingOrder, [typeId]: orderedIds });
  };

  const performSaveOrder = async (typeId: number) => {
    const orderedIds = pendingOrder[typeId];
    if (!orderedIds) return;

    setSavingOrderTypeId(typeId);
    setError("");
    setNotice("");
    try {
      await Channel.Reorder({ typeId, orderedIds });
      setNotice("Channel order saved successfully.");
      setPendingOrder((current) => {
        const next = { ...current };
        delete next[typeId];
        return next;
      });
      await load();
    } catch (reorderError) {
      setError(errorMessage(reorderError, "Could not save the channel order."));
      setPendingOrder((current) => {
        const next = { ...current };
        delete next[typeId];
        return next;
      });
      await load();
    } finally {
      setSavingOrderTypeId(null);
    }
  };

  const saveOrder = (typeId: number) => {
    setConfirmModal({
      message: "Do you want to save the new channel order?",
      onOk: () => performSaveOrder(typeId)
    });
  };

  const discardOrder = (typeId: number) => {
    setPendingOrder((current) => {
      const next = { ...current };
      delete next[typeId];
      return next;
    });
  };

  const groupedChannels = channels.reduce((groups: { [key: number]: IManagedChannel[] }, channel) => {
    if (!groups[channel.typeId]) groups[channel.typeId] = [];
    groups[channel.typeId].push(channel);
    return groups;
  }, {});

  const applyPendingOrder = (typeId: number, members: IManagedChannel[]): IManagedChannel[] => {
    const orderedIds = pendingOrder[typeId];
    if (!orderedIds) return members;
    const byId = new Map(members.map((channel) => [channel.id, channel]));
    return orderedIds.map((id) => byId.get(id)!).filter(Boolean);
  };

  const knownTypeIds: { [key: number]: boolean } = {};
  const sections = channelTypes.map((type) => {
    knownTypeIds[type.id] = true;
    const members = groupedChannels[type.id] || [];
    const activeMembers = applyPendingOrder(type.id, members.filter((channel) => channel.active));
    const inactiveMembers = members.filter((channel) => !channel.active);
    return { id: type.id, name: type.name, activeMembers, inactiveMembers };
  });

  channels.forEach((channel) => {
    if (!knownTypeIds[channel.typeId]) {
      const members = groupedChannels[channel.typeId] || [];
      sections.push({
        id: channel.typeId,
        name: channel.channelTypeName,
        activeMembers: applyPendingOrder(channel.typeId, members.filter((member) => member.active)),
        inactiveMembers: members.filter((member) => !member.active)
      });
      knownTypeIds[channel.typeId] = true;
    }
  });

  return (
    <main className="wrapperApp channels-management">
      {confirmModal ? (
        <Modal
          title="Confirmation"
          message={confirmModal.message}
          className="channels-management__modal"
          onOk={() => {
            confirmModal.onOk();
            setConfirmModal(null);
          }}
          onCancel={() => setConfirmModal(null)}
        />
      ) : null}
      <ContentHeader title="Channels Management" breadcrumb={["Channels Management"]} />
      <div id="content">
        <section className="content__block">
          <div className="content__header">
            <h2 className="content__title">CREATE, EDIT OR ACTIVATE CHANNELS</h2>
            <div className="channels-management__header-actions">
              <label className="channels-management__filter">
                <input
                  type="checkbox"
                  checked={showInactive}
                  onChange={(event) => setShowInactive(event.target.checked)}
                />
                Show inactive channels
              </label>
              <button type="button" className="button button--blue channels-management__add" onClick={openCreate}>
                Add Channel
              </button>
            </div>
          </div>

          <p className="channels-management__hint">
            Drag-and-drop reordering is available for active channels only. Inactive channels stay at the end of the list.
          </p>

          {error ? <p className="channels-management__message channels-management__message--error" role="alert">{error}</p> : null}
          {notice ? <p className="channels-management__message channels-management__message--success" role="status">{notice}</p> : null}

          {formOpen ? (
            <form className="channels-management__form" onSubmit={handleSubmit}>
              <h3>{editing ? "Edit Channel" : "Add Channel"}</h3>
              <div className="channels-management__fields">
                <label>
                  <span className="form__label">Channel Type</span>
                  {editing ? (
                    <input type="text" value={editing.channelTypeName} disabled readOnly />
                  ) : (
                    <select
                      value={form.typeId}
                      required
                      onChange={(event) => setForm({ ...form, typeId: event.target.value })}
                    >
                      <option value="">Select...</option>
                      {channelTypes.map((type) => <option key={type.id} value={type.id}>{type.name}</option>)}
                    </select>
                  )}
                </label>
                <label>
                  <span className="form__label">Name</span>
                  <input
                    type="text"
                    value={form.name}
                    required
                    onChange={(event) => setForm({ ...form, name: event.target.value })}
                  />
                </label>
              </div>
              <div className="channels-management__form-actions">
                <button type="submit" className="button button--blue" disabled={saving}>{saving ? "Saving..." : "Save"}</button>
                <button type="button" className="button button--gray" onClick={closeForm} disabled={saving}>Cancel</button>
              </div>
            </form>
          ) : null}

          {loading ? <Loading /> : (
            <div className="channels-management__groups">
              {sections.map((section) => {
                const hasPendingOrder = Boolean(pendingOrder[section.id]);
                const canReorder = section.activeMembers.length > 1;
                return (
                  <section className="channels-management__group" key={section.id}>
                    <div className="channels-management__group-title">
                      <h3>{section.name}</h3>
                      {hasPendingOrder ? (
                        <div className="channels-management__order-actions">
                          <button
                            type="button"
                            className="button button--blue"
                            onClick={() => saveOrder(section.id)}
                            disabled={savingOrderTypeId === section.id}
                          >
                            {savingOrderTypeId === section.id ? "Saving..." : "Save order"}
                          </button>
                          <button
                            type="button"
                            className="button button--gray"
                            onClick={() => discardOrder(section.id)}
                            disabled={savingOrderTypeId === section.id}
                          >
                            Discard
                          </button>
                        </div>
                      ) : null}
                    </div>
                    {section.activeMembers.length || (showInactive && section.inactiveMembers.length) ? (
                      <ul>
                        <li className="channels-management__group-header">
                          <span></span>
                          <span>Name</span>
                          <span>Status</span>
                          <span></span>
                          <span></span>
                        </li>
                        {canReorder ? (
                          <SortableChannelList
                            channels={section.activeMembers}
                            inactiveChannels={showInactive ? section.inactiveMembers : []}
                            togglingId={togglingId}
                            onEdit={openEdit}
                            onToggleActive={toggleActive}
                            onOrderChange={(orderedIds) => handleOrderChange(section.id, orderedIds)}
                          />
                        ) : (
                          <>
                            {section.activeMembers.map((channel) => (
                              <li key={channel.id}>
                                <span></span>
                                <span className="channels-management__channel-name">{channel.name}</span>
                                <span className="channels-management__status">Active</span>
                                <button type="button" className="button button--gray" onClick={() => openEdit(channel)}>Edit</button>
                                <button
                                  type="button"
                                  className="button button--red"
                                  onClick={() => toggleActive(channel)}
                                  disabled={togglingId === channel.id}
                                >
                                  {togglingId === channel.id ? "Saving..." : "Deactivate"}
                                </button>
                              </li>
                            ))}
                            {showInactive && section.inactiveMembers.map((channel) => (
                              <li key={channel.id} className="channels-management__channel--inactive">
                                <span></span>
                                <span className="channels-management__channel-name">{channel.name}</span>
                                <span className="channels-management__status">Inactive</span>
                                <button type="button" className="button button--gray" onClick={() => openEdit(channel)}>Edit</button>
                                <button
                                  type="button"
                                  className="button button--blue"
                                  onClick={() => toggleActive(channel)}
                                  disabled={togglingId === channel.id}
                                >
                                  {togglingId === channel.id ? "Saving..." : "Activate"}
                                </button>
                              </li>
                            ))}
                          </>
                        )}
                      </ul>
                    ) : <p className="channels-management__empty">No channels in this type.</p>}
                  </section>
                );
              })}
              {!sections.length ? <p className="channels-management__empty">No channel types are available.</p> : null}
            </div>
          )}
        </section>
      </div>
    </main>
  );
};

export default ChannelsManagement;
